import { getCloseIcon, getReadAloudIcon, getWarningIcon } from '@utils/icons';
import i18n from '@i18n';
import { setLanguage } from '@i18n';
import { SearchFallback, Feedback } from 'types';
import config from '@config';
import { AexResponseOptions } from 'types';
import state from '@state';
import { deleteAexResponse } from '@utils/sessionStorage';
import { initReadAloud } from '@response/readAloud';

/**
 * Creates and appends an AgentEx response element to the target container.
 *
 * @param options - Configuration options for the response
 * @returns The created response element
 */
export async function AexResponse({
	target,
	position,
	type,
	responseWrapperClass,
	containerClass,
	responseButtonClass,
	index,
	id,
	content = {},
	searchFallback = false,
	feedback = false,
	rtl = false,
	readAloud = false,
	lang,
	restored = false,
	storageSelector,
}: AexResponseOptions): Promise<HTMLElement> {
	let langAttribute = null;

	if (lang) {
		const siteLanguage = document.documentElement.getAttribute('lang');

		if (siteLanguage !== lang) {
			langAttribute = lang;
			await setLanguage(lang);
		}
	}

	let response = document.createElement('div');
	response.className = 'aex-response';

	response.classList.add(`aex-response-${index}`);
	if (containerClass) {
		response.classList.add(containerClass);
	}

	if (langAttribute) {
		response.setAttribute('lang', langAttribute);
	}

	if (type === 'error') {
		response.setAttribute('role', 'alert');
	} else {
		response.setAttribute('role', 'region');
	}

	response.setAttribute('tabindex', '-1');

	if (rtl) {
		response.setAttribute('dir', 'rtl');
	}

	if (id) {
		response.setAttribute('aria-labelledby', id + '-header');
		response.setAttribute('id', id);
	}

	if (type === 'loading') {
		const responseLoading = document.createElement('div');
		responseLoading.className = 'aex-response__loading';

		const responseLoadingSpinner = document.createElement('div');
		responseLoadingSpinner.className = 'aex-response__loading-spinner';
		responseLoading.appendChild(responseLoadingSpinner);

		const responseLoadingText = document.createElement('p');
		responseLoadingText.className = 'aex-response__loading-text';

		if (id) {
			responseLoadingText.setAttribute('id', id);
		}

		responseLoadingText.innerHTML = content.essentialInformation || i18n.t('loadingText');
		responseLoading.appendChild(responseLoadingText);

		const responseCloseButton = document.createElement('button');
		responseCloseButton.className = 'aex-response__close-button';
		responseCloseButton.innerHTML = getCloseIcon();
		responseLoading.appendChild(responseCloseButton);

		response.appendChild(responseLoading);

		setTimeout(() => {
			responseLoadingText.innerHTML = `<span role="status">${i18n.t('loadingTextChecking')}</span>`;
		}, 3000);

		setTimeout(() => {
			responseLoadingText.innerHTML = `<span role="status">${i18n.t('loadingTextWriting')}</span>`;
		}, 7000);


	} else if (type === 'error') {
		response.classList.add('aex-response__error');

		const responseErrorHeading = document.createElement('div');
		responseErrorHeading.className = 'aex-response__header';

		const responseErrorHeadingText = document.createElement('span');
		responseErrorHeadingText.classList.add('aex-response__heading-text');

		if (id) {
			responseErrorHeadingText.setAttribute('id', id + '-header');
		}
		responseErrorHeadingText.innerHTML = 'Error';

		const responseErrorCloseButton = document.createElement('button');
		responseErrorCloseButton.className = 'aex-response__close-button';
		responseErrorCloseButton.innerHTML = getCloseIcon();

		responseErrorHeading.appendChild(responseErrorHeadingText);
		responseErrorHeading.appendChild(responseErrorCloseButton);
		response.appendChild(responseErrorHeading);

		const responseErrorContent = document.createElement('div');
		responseErrorContent.className = 'aex-response-error';
		responseErrorContent.innerHTML =
			content.errorText || i18n.t('error.requestIssue');
		response.appendChild(responseErrorContent);
	} else {
		const responseHeader = document.createElement('div');
		responseHeader.className = 'aex-response__header';

		if (content.formattedQuery) {
			response.setAttribute('data-formatted-query', content.formattedQuery);
		}

		if (readAloud) {
			const responseReadAloudButton = document.createElement('button');
			responseReadAloudButton.className = 'aex-response__read-aloud-button';
			responseReadAloudButton.setAttribute('aria-pressed', 'false');
			responseReadAloudButton.innerHTML = getReadAloudIcon();
			responseHeader.appendChild(responseReadAloudButton);
		}

		const responseHeaderText = document.createElement('p');
		responseHeaderText.classList.add('aex-response__heading-text');

		if (id) {
			responseHeaderText.setAttribute('id', id + '-header');
		}
		responseHeaderText.innerHTML = `${i18n.t('responsePrepend')}: <span class="aex-response__query">${content.query || ''}</span>`;

		const responseCloseButton = document.createElement('button');
		responseCloseButton.className = 'aex-response__close-button';
		responseCloseButton.innerHTML = getCloseIcon();

		responseHeader.appendChild(responseHeaderText);
		responseHeader.appendChild(responseCloseButton);
		response.appendChild(responseHeader);

		if (content.timeSensitiveAlert) {
			const responseAlert = document.createElement('div');
			responseAlert.className = 'aex-response__alert';

			const responseAlertText = document.createElement('p');
			responseAlertText.className = 'aex-response__text';
			responseAlertText.innerHTML = `${getWarningIcon()}${content.timeSensitiveAlert}`;
			responseAlert.appendChild(responseAlertText);
			response.appendChild(responseAlert);
		}

		if (content.primaryActions && content.primaryActions.length > 0) {
			const responseActions = document.createElement('div');
			responseActions.className = 'aex-response__actions';

			content.primaryActions.forEach((action) => {
				const actionButton = document.createElement('a');
				actionButton.className = responseButtonClass ? `button ${responseButtonClass}` : 'button button--primary';
				actionButton.setAttribute('href', action.url);
				actionButton.innerHTML = action.text;
				responseActions.appendChild(actionButton);
			});

			response.appendChild(responseActions);
		}

		const responseContent = document.createElement('div');
		responseContent.className = 'aex-response__content';

		responseContent.innerHTML = content.essentialInformation || '';

		if (searchFallback && config.SearchFallback) {
			const searchFallbackContent = getSearchFallback(content, config.SearchFallback);
			responseContent.innerHTML += searchFallbackContent;
		}

		if (feedback && config.feedback) {
			const feedbackContent = getFeedback(config.feedback);
			responseContent.innerHTML += feedbackContent;
		}

		responseContent.innerHTML += `<div class="aex-disclaimer">✨ ${i18n.t('disclaimer')}</div>`;

		if (config.showSources && Array.isArray(content?.sources) && content.sources.length > 0) {
			const nonDuplicateSources = content.sources.filter((item, index, self) =>
				index === self.findIndex(other => other.url === item.url),
			);

			let sourcesHTML = `<div class="aex-sources-wrapper"><button class="button button--primary button--small" aria-expanded="false" id="aex-sources-toggle-${index}" aria-controls="aex-sources-${index}">Show sources</button><nav class="aex-sources" id="aex-sources-${index}" aria-labelledby="aex-sources-${index}-label" role="region"><p id="aria-sources-${index}-label">${i18n.t('sources.title')}:</p><ol>`;

			nonDuplicateSources.forEach((item, index) => {
				const linkText = item.title || `${i18n.t('sources.sourceStart')} ${content.query} ${i18n.t('sources.sourceStart')} ${index + 1}`;
				sourcesHTML += `<li><a href="${item.url}">${linkText || `Source ${index + 1}`}</a></li>`;
			});
			sourcesHTML += '</ol></nav></div>';

			responseContent.innerHTML += sourcesHTML;
		}

		response.appendChild(responseContent);
	}

	if (responseWrapperClass) {
		const wrapper = document.createElement('div');
		wrapper.classList.add(responseWrapperClass);
		wrapper.classList.add('aex-response-wrapper');
		wrapper.appendChild(response);
		response = wrapper;
	}

	const targetElement = typeof target === 'string' ? document.querySelector(target) : target;

	if (targetElement) {
		switch (position) {
		case 'before':
			targetElement.insertAdjacentElement('beforebegin', response);
			break;
		case 'after':
			targetElement.insertAdjacentElement('afterend', response);
			break;
		case 'prepend':
			targetElement.insertAdjacentElement('afterbegin', response);
			break;
		case 'append':
			targetElement.appendChild(response);
			break;
		case 'replace':
		default:
			targetElement.innerHTML = '';
			targetElement.append(response);
			break;
		}
	}

	if (!restored) {
		if (responseWrapperClass) {
			(response.querySelector('.aex-response') as HTMLInputElement)?.focus();
		} else {
			response.focus();
		}
	}

	// Event Listeners for closing
	response.querySelector('.aex-response__close-button')?.addEventListener('click', () => {
		const parent = response.parentNode;

		if (parent instanceof Element && parent.classList.contains('aex-response-wrapper')) {
			parent.remove();
		} else {
			response.remove();
		}

		if (storageSelector) {
			const searchInput = document.querySelector(`${storageSelector} input[type="search"], ${storageSelector} input[type="text"]`);

			if (searchInput instanceof HTMLInputElement) {
				searchInput.focus();
				searchInput.value = '';
			}
		}

		if (restored && storageSelector && config.persistResults) {
			deleteAexResponse(storageSelector);
		}

		if (state.currentAudio) {
			state.currentAudio.pause();
			state.currentAudio = null;
		}
	});

	// Source toggle event listener
	if (config.showSources) {
		const sourceToggle = document.getElementById(`aex-sources-toggle-${index}`);
		const sourceContent = document.getElementById(`aex-sources-${index}`);

		if (sourceToggle instanceof HTMLButtonElement && sourceContent instanceof HTMLElement) {
			sourceToggle.addEventListener('click', function () {
				const expanded = sourceToggle.getAttribute('aria-expanded') === 'true';
				sourceToggle.setAttribute('aria-expanded', String(!expanded));
				sourceToggle.innerHTML = sourceToggle.getAttribute('aria-expanded') === 'true' ? i18n.t('sources.hide') : i18n.t('sources.show');
				sourceContent.classList.toggle('aex-sources-open');
			});
		}
	}

	if (readAloud && content.essentialInformation) {
		initReadAloud(content.essentialInformation);
	}

	return response;
}

/**
 * Formats the search fallback container
 *
 * @param content - The response content object.
 * @returns The formatted HTML as a string.
 */
function getSearchFallback(content: any, SearchFallback: SearchFallback): string {
	const maxLength = SearchFallback.searchQueryLength || 255;
	const queryString = content.keywordQuery
		? encodeURIComponent(content.keywordQuery.slice(0, maxLength))
		: content.shortRawQuery
			? encodeURIComponent(content.shortRawQuery.slice(0, maxLength))
			: '';

	let fallbackText = '<div class="aex-search-fallback"><p>' + (config.SearchFallback?.fallbackText || i18n.t('fallback.text')) + '&nbsp;';
	const separator = config.SearchFallback?.searchURLPath?.includes('?') ? '&' : '?';
	fallbackText += `<a href="${config.SearchFallback?.searchURLPath}${separator}${config.SearchFallback?.searchQueryParamName}=${queryString}&forcereload=true">${config.SearchFallback?.fallbackLinkText || i18n.t('fallback.linkText')}</a>`;
	fallbackText += '</p></div>';
	return fallbackText;
}

/**
 * Formats the feedback container
 *
 * @param feedback - The response feedback object.
 * @returns The formatted HTML as a string.
 */
function getFeedback(feedback: Feedback): string {
	let feedbackText = '<div class="aex-search-feedback"><p>';

	if (feedback.feedbackText !== undefined) {
		feedbackText += feedback?.feedbackText + '&nbsp;';
	}

	if (!feedback.feedbackLinkPath || !feedback.feedbackLinkText) {
		return '';
	}

	feedbackText += `<a href="${feedback?.feedbackLinkPath}">${feedback?.feedbackLinkText}</a>`;
	feedbackText += '</p></div>';
	return feedbackText;
}
